type InteractionEvent =
  | "pointerdown"
  | "keydown"
  | "wheel"
  | "touchstart"
  | "scroll";

type AfterInteractionOptions = {
  fallbackMs?: number;
  /** Domyślnie wszystkie — hero ST powinien używać tylko scroll/wheel. */
  events?: InteractionEvent[];
};

const DEFAULT_EVENTS: InteractionEvent[] = [
  "pointerdown",
  "keydown",
  "wheel",
  "touchstart",
  "scroll",
];

/**
 * Odpalia callback po pierwszej interakcji.
 * Idle w PSI psuje TBT — dlatego NIE używamy idle.
 */
export function afterInteraction(
  callback: () => void,
  fallbackMsOrOptions: number | AfterInteractionOptions = 15000,
): () => void {
  const options: AfterInteractionOptions =
    typeof fallbackMsOrOptions === "number"
      ? { fallbackMs: fallbackMsOrOptions }
      : fallbackMsOrOptions;

  const fallbackMs = options.fallbackMs ?? 15000;
  const events = options.events ?? DEFAULT_EVENTS;

  let done = false;
  let fallbackId: number | null = null;

  const run = () => {
    if (done) return;
    done = true;
    detach();
    callback();
  };

  const onEvent = () => run();

  const detach = () => {
    for (const event of events) {
      window.removeEventListener(event, onEvent);
    }
    if (fallbackId != null) {
      window.clearTimeout(fallbackId);
      fallbackId = null;
    }
  };

  for (const event of events) {
    window.addEventListener(event, onEvent, { once: true, passive: true });
  }

  fallbackId = window.setTimeout(run, fallbackMs);

  return detach;
}

/** Tylko scroll/wheel — bez kliknięcia (klik + pin ScrollTrigger = skok layoutu). */
export function afterScroll(
  callback: () => void,
  fallbackMs = 15000,
): () => void {
  return afterInteraction(callback, {
    fallbackMs,
    events: ["wheel", "scroll"],
  });
}
